/**
 * The VM size each new server is built with. The size catalog (data/sizeCatalog.ts) says what the page shows: every RDS VM is the same fixed
 * size whatever the pool tier is, and the DB VM is a fixed constant. The backend checks them against what the chosen region offers
 * (`GET /infrastructure/vm-sizes/`, rules/vm_sizes.py). Where the region cannot build a size (the RDS VM's or the DB VM's), the answer is
 * the nearest series of the same size, OFFERED (`substituted`): the user chooses whether to continue with it or to ask Platform Engineering
 * to raise the quota (`VmSizeChoices`), and only an agreed series is built. These are the pure helpers around it.
 */
import { resolveVm, type VmKind } from '@/data/sizeCatalog'
import { effectiveServerMode, type InfraWizardData } from '@/stores/infrastructureWizard'
import type { VmSizeAgreement, VmSizeCheck, VmSizeChoice, VmSizeChoices, VmSizesQuery } from '@/types/infrastructure'
import { usesPoolTiers } from '@/utils/sizing'

/**
 * Which new VMs are built: the RDS VM when the RDS server is Dedicated, the DB VM when a SQL server VM is (a Dedicated DB server on
 * the Domain level; the AppCentral level's Azure SQL is not sized). Only the products that use the pool tiers have them.
 */
export function builtServers(data: Pick<InfraWizardData, 'product' | 'level' | 'rdsServer' | 'dbServer'>): { rds: boolean; db: boolean } {
  if (!usesPoolTiers(data.product)) return { rds: false, db: false }
  return {
    rds: effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated',
    db: data.level === 'domain' && effectiveServerMode(data.product, data.dbServer.mode, 'db') === 'dedicated',
  }
}

const KIND_LABELS: Readonly<Record<VmKind, string>> = { rds: 'RDS VM', db: 'DB VM' }

/** Every size as the catalog shows it, unchecked: for an environment that cannot be asked, and for what the page shows before it is. */
export function uncheckedVmSizes(query: Pick<VmSizesQuery, 'poolTier' | 'rds' | 'db' | 'hosts'>): VmSizeCheck[] {
  const sizes: VmSizeCheck[] = []
  for (const [kind, built] of [['rds', query.rds], ['db', query.db]] as const) {
    if (!built) continue
    const spec = resolveVm(kind, query.poolTier)
    sizes.push({
      tier: kind,
      label: KIND_LABELS[kind],
      size: query.poolTier,
      requested: spec.sku,
      effective: spec.sku,
      status: 'unchecked',
      cause: '',
      message: '',
      vcpus: spec.vcpus,
      memoryGb: spec.memoryGb,
      count: kind === 'rds' ? Math.max(query.hosts, 1) : 1,
    })
  }
  return sizes
}

/** One kind's size, when it is among the checked ones. */
export const sizeOfTier = (sizes: readonly VmSizeCheck[] | undefined, tier: VmKind): VmSizeCheck | undefined => sizes?.find((size) => size.tier === tier)

/** "Standard_E16ds_v6 (instead of Standard_E16ds_v5)" for a substituted size, else the size built. */
export function sizeText(check: Pick<VmSizeCheck, 'requested' | 'effective' | 'status'>): string {
  return check.status === 'substituted' ? `${check.effective} (instead of ${check.requested})` : check.effective
}

/** What the choices are made for: the environment and the region. A choice made for another one does not count. */
export const sizeWhere = (data: Pick<InfraWizardData, 'environment' | 'region'>): string => `${data.environment?.id ?? ''}/${data.region?.id ?? ''}`

/** What the user chose for this size, or null when nothing was chosen for exactly this one (the same server, region, and the same two sizes). */
export function choiceOf(check: Pick<VmSizeCheck, 'tier' | 'requested' | 'effective'>, choices: VmSizeChoices, where: string): VmSizeChoice | null {
  const entry = choices[check.tier]
  return entry && entry.where === where && entry.requested === check.requested && entry.effective === check.effective ? entry.choice : null
}

/** What the user has to do before the step continues, for a size that cannot be built as shown. */
export const wayOutText = (check: Pick<VmSizeCheck, 'cause'>): string =>
  check.cause === 'quota' ? 'ask Platform Engineering to raise the quota' : 'choose another region'

/**
 * Why Next waits: a size nothing can replace (until another region is chosen or the quota is raised), and a replacement the user has not
 * agreed to yet (until they choose to continue with it; choosing to ask for more quota keeps it waiting).
 */
export function vmSizeProblems(sizes: readonly VmSizeCheck[] | undefined, choices: VmSizeChoices = {}, where = ''): string[] {
  return (sizes ?? []).flatMap((size) => {
    if (size.status === 'unavailable') return [size.message]
    if (size.status === 'substituted' && choiceOf(size, choices, where) !== 'use') {
      return [`${size.label}: choose whether to continue with ${size.effective} or to ${wayOutText(size)}.`]
    }
    return []
  })
}

/** The series the user agreed to build instead, for the servers that are built: what the request carries. */
export function agreedVmSizes(
  data: Pick<InfraWizardData, 'product' | 'level' | 'rdsServer' | 'dbServer' | 'environment' | 'region' | 'vmSizeChoices'>,
): Partial<Record<VmKind, VmSizeAgreement>> {
  const built = builtServers(data)
  const where = sizeWhere(data)
  const agreed: Partial<Record<VmKind, VmSizeAgreement>> = {}
  for (const kind of ['rds', 'db'] as const) {
    const entry = data.vmSizeChoices[kind]
    if (built[kind] && entry?.choice === 'use' && entry.where === where) agreed[kind] = { requested: entry.requested, effective: entry.effective }
  }
  return agreed
}
